import { Feature, FeatureCollection } from 'geojson';
import mapboxgl from 'mapbox-gl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Map, MapLayerMouseEvent, MapRef, NavigationControl, ScaleControl, Source } from 'react-map-gl';
import styled from 'styled-components';
import {
    AcquisitionPopUpInfo,
    FeatureCollectionAcquisitions,
    Insights,
    MetaDataFeatureCollection,
} from '../../utils/interfaces';
import constants, { FLY_TO_DURATION, INSIGHT_ZOOM_LEVEL, WEB_MAP_TILE_SERVICE_MIN_ZOOM } from '../constants';
import { TimeInAcqusitionPopUp } from './Popup';

const MapContainer = styled.div<{ isLoadingData: boolean }>`
    width: 100%;
    height: 100%;
    opacity: ${(props) => (props.isLoadingData ? 0.5 : 1)};
`;

const getUniqueIds = (items?: mapboxgl.MapboxGeoJSONFeature[]): string[] => {
    const uniqueIds = new Set<string>();
    if (!items) {
        return [];
    }
    for (const item of items) {
        if (item.layer && item.layer.id) {
            uniqueIds.add(item.layer.id);
        }
    }
    return Array.from(uniqueIds);
};

export interface ILayer {
    id: string;
    name: string;
    static?: boolean;
    data: FeatureCollection | undefined;
    fill?: unknown;
    stroke?: string;
    filter?: unknown[];
    fillHover?: 'string';
    strokeHover?: 'string';
    type: 'line' | 'circle' | 'fill';
    fillOpacity?: number;
    paint?: unknown;
    popupType?: 'detection' | 'toi';
    circleRadius?: number;
    cluster?: boolean;
    maxzoom?: number;
}

type RMap = {
    insights: Insights | undefined;
    clouds: unknown | undefined;
    customData: unknown | undefined;
    metaData: MetaDataFeatureCollection | undefined;
    centerPosition: [number, number] | null;
    initalZoom: number;
    activeDate: Date | undefined;
    availableDates: Date[] | undefined;
    isInsightsLoading: boolean;
    map: MapRef | null;
    setMap: (map: MapRef | null) => void;
    visibleLayers: string[];
    selectedFeature: Feature | null;
    setSelectedFeature: (feature: Feature | null) => void;
    setHoveredFeature: (feature: Feature | null) => void;
    hoveredFeature: Feature | null;
    detectionConfidence: number;
    acqusitionData: FeatureCollectionAcquisitions | undefined;
    showSentinelMapTiles: boolean;
};
const RMap: React.FC<RMap> = ({
    insights,
    clouds,
    customData,
    isInsightsLoading,
    setMap,
    selectedFeature,
    setSelectedFeature,
    visibleLayers,
    setHoveredFeature,
    hoveredFeature,
    metaData,
    centerPosition,
    initalZoom,
    activeDate,
    detectionConfidence,
    acqusitionData,
    showSentinelMapTiles,
}) => {
    const [popupInfo, setPopupInfo] = useState<AcquisitionPopUpInfo | null>(null);

    const hoveredItem = useRef<{ layer_id: string; feature_id: string | number | undefined } | null>(null);
    const viewport = {
        latitude: centerPosition ? centerPosition[0] : 57.33870347771735,
        longitude: centerPosition ? centerPosition[1] : 2.042840975438542,
        zoom: initalZoom,
    };

    const handleMapClick = (event: MapLayerMouseEvent) => {
        if (event.features && event.features.length > 0) {
            const uniqueIds = getUniqueIds(event.features);
            let features;
            if (event.features.length > 1 && uniqueIds.length > 1) {
                features = event.features?.filter(
                    (f) => f.layer.id !== 'acqusition_data' // Filter out acqusition_data in case some are overlapping
                );
            } else {
                features = event.features;
            }
            const clickedFeature = features[0];
            const source = clickedFeature.source;
            if (source === 'acqusition_data' && clickedFeature.properties) {
                setPopupInfo(null);
                // If the acqusition layer is above a footprint, dont render popup.
                const hiddenFeatures = mapRef.current?.queryRenderedFeatures(event.point);
                if (getUniqueIds(hiddenFeatures).includes('footprints')) {
                    return;
                }
                const startTime = clickedFeature.properties.startTime;
                const satellite = clickedFeature.properties.satellite;
                setTimeout(() => {
                    // have to delay to enable state change
                    setPopupInfo({
                        lon: event.lngLat.lng,
                        lat: event.lngLat.lat,
                        timestamp: new Date(startTime),
                        satellite: satellite,
                    });
                }, 0.01);
                return;
            }
            const insightFeature =
                insights && source in insights
                    ? (insights[source as keyof Insights] as FeatureCollection)?.features?.find(
                          (feature: Feature) => feature.properties?.uuid === clickedFeature.properties?.uuid
                      )
                    : undefined;
            if (insightFeature) {
                setSelectedFeature(insightFeature);
            } else {
                setSelectedFeature(null);
            }
        }
    };

    useEffect(() => {
        if (selectedFeature) {
            const lat = selectedFeature.properties?.lat;
            const lon = selectedFeature.properties?.lon;
            if (mapRef.current && lat && lon) {
                const bounds = mapRef.current.getBounds();
                if (!(bounds.contains([lon, lat]) && mapRef.current.getZoom() > INSIGHT_ZOOM_LEVEL - 1.5)) {
                    mapRef.current?.flyTo({
                        center: [lon, lat],
                        duration: FLY_TO_DURATION,
                        zoom: Math.max(mapRef.current.getZoom(), INSIGHT_ZOOM_LEVEL),
                    });
                }
            }
        }
    }, [selectedFeature]);

    const onHover = useCallback(
        (event: MapLayerMouseEvent) => {
            const features = event.features?.filter(
                (f) => f.layer.id !== 'acqusition_data' // Filter out layers not to react on hover
            );
            if (features && features.length === 0 && hoveredItem.current !== null) {
                mapRef.current?.setFeatureState(
                    { source: hoveredItem.current.layer_id, id: hoveredItem.current.feature_id },
                    { hover: false }
                );
                hoveredItem.current = null;
                setHoveredFeature(null);
                return;
            }

            if (features && features.length > 0) {
                if (hoveredItem.current !== null) {
                    mapRef.current?.setFeatureState(
                        { source: hoveredItem.current.layer_id, id: hoveredItem.current.feature_id },
                        { hover: false }
                    );
                }

                hoveredItem.current = {
                    layer_id: features[0].layer.id,
                    feature_id: features[0].id,
                };
                setHoveredFeature(features[0]);
                mapRef.current?.setFeatureState({ source: features[0].layer.id, id: features[0].id }, { hover: true });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const mapRef = React.useRef<MapRef | null>(null);

    const confidenceFilter: [string, (string | string[])[], number] = useMemo(() => {
        return ['>=', ['to-number', ['get', 'object_class_score']], detectionConfidence / 100];
    }, [detectionConfidence]);

    const connectedVesselsFilter = useMemo(() => {
        const connectedLayers = visibleLayers.filter((item) => ['matched_vessels', 'undetected_ais'].includes(item));

        return [
            'all',
            ['match', ['at', 0, ['get', 'LayerIds']], connectedLayers, true, false],
            ['match', ['at', 1, ['get', 'LayerIds']], connectedLayers, true, false],
        ];
    }, [visibleLayers]);

    useEffect(() => {
        if (mapRef.current && centerPosition) {
            mapRef.current?.flyTo({ center: centerPosition, duration: FLY_TO_DURATION, zoom: initalZoom });
        }
    }, [mapRef, centerPosition, initalZoom]);

    const layers = useMemo(() => {
        const curr_layers: ILayer[] = [];
        if (acqusitionData) {
            curr_layers.push({
                id: `acqusition_data`,
                name: 'acqusition_data',
                data: acqusitionData,
                paint: { 'fill-color': constants.colors.markers.acquisitions.fill, 'fill-opacity': 0.25 },
                type: 'fill',
            });
        }
        if (metaData && metaData.features) {
            curr_layers.push({
                id: 'aoi',
                name: 'aoi',
                type: 'line',
                paint: {
                    'line-color': '#00A1FF',
                },
                data: metaData?.features
                    ? ({ type: 'FeatureCollection', features: metaData.features } as FeatureCollection)
                    : { type: 'FeatureCollection', features: [] },
            });
        }
        if (!insights) return curr_layers;

        const colors = constants.colors.markers;

        // Step 1: Combine matched_vessels and undetected_ais
        const matched = insights.matched_vessels?.features || [];
        const undetected = insights.undetected_ais?.features || [];
        const combinedFeatures = [...matched, ...undetected];

        // Step 2: Group by `mmsi`
        const groupByMMSI = (features: Feature[]) => {
            const groups: Record<string, Feature[]> = {};
            features.forEach((feature) => {
                const mmsi = feature.properties?.mmsi;
                if (mmsi) {
                    if (!groups[mmsi]) groups[mmsi] = [];
                    groups[mmsi].push(feature);
                }
            });
            return groups;
        };

        // Step 3: Create LineStrings from groups
        const createLinesFromGroups = (groups: Record<string, Feature[]>) => {
            const lines: Feature[] = [];

            Object.values(groups).forEach((features) => {
                if (features.length > 1) {
                    // Sort features by timestamp ascending (in order to have the lines drawn the correct way)
                    features.sort((a, b) => {
                        const t1 = new Date(a.properties?.timestamp ?? 0).getTime();
                        const t2 = new Date(b.properties?.timestamp ?? 0).getTime();
                        return t1 - t2;
                    });

                    const coordinates = features
                        .map((f) => f.geometry.coordinates)
                        .filter((coords) => Array.isArray(coords)); // Just in case

                    const matching_labels = features.map((f) => f.properties?.matching_label);

                    const STATUS_MAP: Record<string, string> = {
                        matched: 'matched_vessels',
                        undetected: 'undetected_ais',
                    };

                    const mapStatuses = (statuses: string[]) => {
                        return statuses.map((status) => STATUS_MAP[status]).filter((mapped) => mapped !== undefined);
                    }; // Filter out undefineds if unexpected values exist
                    lines.push({
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates,
                        },
                        properties: { LayerIds: mapStatuses(matching_labels) },
                    });
                }
            });

            return {
                type: 'FeatureCollection',
                features: lines,
            };
        };

        const groupedByMMSI = groupByMMSI(combinedFeatures);
        const connectedLinesData = createLinesFromGroups(groupedByMMSI);

        const new_layers: ILayer[] = [
            {
                id: `custom_data`,
                name: 'custom_data',
                data: customData!,
                paint: { 'fill-color': constants.colors.pallet.lightGreen, 'fill-opacity': 0.3 },
                type: 'fill',
            },
            {
                id: `footprints`,
                name: 'footprints',
                static: true,
                data: insights!.product_footprints,
                paint: {
                    'fill-color': [
                        'match',
                        ['get', 'processing_status'],
                        'completed',
                        '#28A745',
                        'started',
                        '#00A1FF',
                        'waiting',
                        '#FFD700',
                        'failed',
                        '#c72626',
                        '#00A1FF', // Default color in overwatch without status-coloring
                    ],
                    'fill-opacity': 0.3,
                },
                type: 'fill',
                maxzoom: showSentinelMapTiles ? 9 : 17,
            },
            {
                id: `cloudmasks`,
                name: 'cloudmasks',
                static: true,
                data: clouds!,
                paint: { 'fill-color': '#c0e0f3', 'fill-opacity': 0.2 },
                type: 'fill',
            },
            {
                id: `static_objects`,
                name: 'static_objects',
                static: true,
                data: insights!.static_objects,
                paint: {
                    'circle-color': '#000',
                    'circle-stroke-color': '#00A1FF',
                    'circle-stroke-width': 1,
                },
                type: 'circle',
                popupType: 'detection',
            },
            {
                id: `undetected_ais`,
                name: 'undetected_ais',
                data: insights!.undetected_ais,
                paint: {
                    'circle-color': colors.undetectedAis.fill,
                    'circle-stroke-color': colors.undetectedAis.stroke,
                    'circle-stroke-width': 1,
                },
                type: 'circle',
                popupType: 'detection',
                circleRadius: 3,
            },
            {
                id: `dark_vessels`,
                name: 'dark_vessels',
                data: insights!.dark_vessels,
                paint: {
                    'circle-color': colors.darkShips.fill,
                    'circle-stroke-color': colors.darkShips.stroke,
                    'circle-stroke-width': 1,
                },
                type: 'circle',
                popupType: 'detection',
            },
            {
                id: `matched_vessels`,
                name: 'matched_vessels',
                data: insights!.matched_vessels,
                paint: {
                    'circle-color': colors.matchedVessels.fill,
                    'circle-stroke-color': colors.matchedVessels.stroke,
                    'circle-stroke-width': 1,
                },
                type: 'circle',
                popupType: 'detection',
            },
            {
                id: `connected_vessels`,
                name: 'connected_vessels',
                data: connectedLinesData,
                type: 'line',
                paint: {
                    'line-color': colors.matchedVessels.fill,
                    'line-width': 2,
                    'line-opacity': 0.8,
                },
            },
            {
                id: `connected_vessels_arrow`,
                type: 'symbol',
                data: connectedLinesData,
                layout: {
                    'symbol-placement': 'line',
                    'icon-image': 'arrow_right',
                    'icon-size': 0.5,
                },
            },
        ];
        if (curr_layers.length > 0) {
            new_layers.unshift(...curr_layers); // Want it on the bottom upon rendering
        }
        return new_layers;
    }, [insights, clouds, metaData, customData, acqusitionData, showSentinelMapTiles]);

    // Filter layers based on visibleLayers prop
    const filteredLayers = useMemo(() => {
        return layers.filter((layer) => visibleLayers.includes(layer.id));
    }, [layers, visibleLayers]);

    const onMapLoad = useCallback(() => {
        if (!mapRef.current) return;
        mapRef.current.loadImage('/images/icons/ArrowRight.png', (error: unknown, image: unknown) => {
            if (error) {
                throw error;
            }
            if (mapRef.current) {
                mapRef.current.addImage('arrow_right', image);
            }
        });
        setMap(mapRef.current);
    }, [setMap]);

    const seamarkSource = {
        type: 'raster',
        tiles: ['https://t1.openseamap.org/seamark/{z}/{x}/{y}.png'],
        minzoom: 0,
        maxzoom: 18,
    };

    const seamarkLayer = {
        id: 'seamark-layer',
        type: 'raster',
        source: 'seamarks',
        paint: {
            // Optional: tweak opacity or brightness if desired
            'raster-opacity': 1.0,
        },
    };

    const activeDateShortForm = activeDate ? activeDate.toISOString().split('T')[0] : '';
    const sentinelImageSource = {
        type: 'raster',
        tiles: [
            `https://services.sentinel-hub.com/ogc/wmts/27b1fd96-7716-4fe4-909e-95c959f273de?showLogo=false&service=WMTS&request=GetTile&format=image/jpeg&layer=NATURAL-COLOR&transparent=false&maxcc=100&&time=${activeDateShortForm}/${activeDateShortForm}&TILEMATRIXSET=PopularWebMercator256&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}`,
        ],
        tileSize: 256,
        minzoom: WEB_MAP_TILE_SERVICE_MIN_ZOOM,
    };
    const sentinelImageLayer = {
        id: 'sentinel-image',
        type: 'raster',
        source: 'sentinel',
        paint: {},
        minzoom: WEB_MAP_TILE_SERVICE_MIN_ZOOM,
    };

    return (
        <MapContainer isLoadingData={isInsightsLoading}>
            <Map
                initialViewState={viewport}
                ref={mapRef}
                onLoad={onMapLoad}
                mapStyle="mapbox://styles/vake-torstein/cl4xzn1gs000p14qgz8nqs0ms"
                mapboxAccessToken="pk.eyJ1IjoidmFrZS10b3JzdGVpbiIsImEiOiJjbDR4emJmbW0xdTRpM21tbG1lbnc3b2JrIn0.V7PXI4I2ndH2mxaJrVCnRw"
                interactiveLayerIds={[
                    'acqusition_data',
                    'static_objects',
                    'undetected_ais',
                    'dark_vessels',
                    'matched_vessels',
                ].filter((id) => visibleLayers.includes(id))} // Intersection of existing interactiveLayerIds and filteredLayers
                onClick={handleMapClick}
                onMouseMove={onHover}
            >
                <ScaleControl />
                <NavigationControl position="bottom-right" />
                {filteredLayers.map((layer) => (
                    <Source key={layer.id} id={layer.id} type="geojson" data={layer.data}>
                        {(() => {
                            if (layer.id === 'dark_vessels') {
                                return <Layer {...layer} filter={confidenceFilter} />;
                            }
                            if (layer.id === 'connected_vessels' || layer.id === 'connected_vessels_arrow') {
                                return <Layer {...layer} filter={connectedVesselsFilter} />;
                            }
                            return <Layer {...layer} />;
                        })()}
                    </Source>
                ))}
                {selectedFeature?.properties && (
                    <Source key="selectedFeature" id="selectedFeature" type="geojson" data={selectedFeature}>
                        <Layer
                            id="selectedFeature"
                            type="circle"
                            paint={{
                                'circle-radius': 12,
                                'circle-stroke-color': '#f0d971',
                                'circle-stroke-width': 2,
                                'circle-opacity': 0.2,
                                'circle-stroke-opacity': 0.4,
                                'circle-color': '#f0d971',
                            }}
                        />
                    </Source>
                )}
                {hoveredFeature && hoveredFeature.properties && (
                    <Source key="hoveredFeature" id="hoveredFeature" type="geojson" data={hoveredFeature}>
                        <Layer
                            id="hoveredFeature"
                            type="circle"
                            paint={{
                                'circle-radius': 12,
                                'circle-stroke-color': '#f0d971',
                                'circle-stroke-width': 2,
                                'circle-opacity': 0.2,
                                'circle-stroke-opacity': 0.4,
                                'circle-color': '#f0d971',
                            }}
                        />
                    </Source>
                )}
                <Source id="seamarks" type="raster" tiles={seamarkSource.tiles}>
                    <Layer {...seamarkLayer} />
                </Source>
                {showSentinelMapTiles && (
                    <Source id="sentinel-image" type="raster" tiles={sentinelImageSource.tiles}>
                        <Layer {...sentinelImageLayer} beforeId="aoi" />
                    </Source>
                )}
                {popupInfo && <TimeInAcqusitionPopUp acqusitionPopUp={popupInfo} setPopupInfo={setPopupInfo} />}
            </Map>
        </MapContainer>
    );
};

export default RMap;
